import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  BRANDCLUB_BRAND_ID,
  BrandclubEntityType,
  CheckoutEligibility,
  getBrandIds,
  IReduxStoreBase,
  isServerSideCheckoutEligible,
  ProductRewardSearchResult,
  useStateWithDep,
  WidgetIdToComponentMapping,
} from "@brandclub/common-ui";
import { BrandclubPageType, Page, Site, Widget } from "@brandclub/types";
import React, { useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getBrandClubForBrandPublic,
  getEntityForLandingPage,
  joinBrandClub,
} from "../../../../api";
import axios, { axiosEdgeCached } from "../../../../utils/axios";

import { BrandClub } from "@brandclub/types";
import _isNil from "lodash/isNil";
import queryString from "qs";

import useDeepCompareEffect from "use-deep-compare-effect";
import { useLocationTrackingParams } from "../../../providers/LocationTrackingProvider";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { buildWidgetProps } from "../../../../utils/pageUtils";
import ErrorFallback from "../../../Error/ErrorFallback";

import {
  getBrandclubEntity,
  getBrandEntity,
} from "../../../../redux/reducers/brandEntity/thunk";

import { useSiteContext } from "../../../SiteProvider";
import ShareProductDialog from "../../../StoreComponents/StoreDialogs/ShareProductDialog";
import ConfigUtils from "../../../../utils/ConfigUtils";
import { useCheckoutDrawer } from "../../../Checkout/useCheckoutDrawer";
import Offers from "../Offers";
import "./BrandStoreLandingPageStyle.scss";
import BrandstoreHomepage from "./BrandstoreHomePage";
import StoreFullScreenLoading from "../../../StoreComponents/StoreFullScreenLoading";
import { getAuthorizationHeader } from "../../../../Auth";
import { addOptimisticShoppingCartItem } from "@/redux/reducers/checkout";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { PageContext } from "./types";
import Dashboard from "../Dashboard";
import DashboardOutlet from "../Dashboard/DashboardOutlet";
import { useApolloClient } from "@apollo/client";
const PurchaseDetail = React.lazy(
  () => import("../Dashboard/Purchase/PurchaseDetail")
);
const PurchaseList = React.lazy(
  () => import("../Dashboard/Purchase/purchaseList")
);
const Activity = React.lazy(() => import("../Dashboard/Rewards/Activity"));
const Balance = React.lazy(() => import("../Dashboard/Rewards/Balance"));
const Expiring = React.lazy(() => import("../Dashboard/Rewards/Expiring"));
const PayoutDetail = React.lazy(
  () => import("../Dashboard/Rewards/PayoutDetail")
);
const Payouts = React.lazy(() => import("../Dashboard/Rewards/Payouts"));
const AffiliateLanding = React.lazy(() => import("../Pages/Affiliate/landing"));
const ContentLanding = React.lazy(() => import("../Pages/Content/landing"));
const PopularLanding = React.lazy(() => import("../Pages/Popular/landing"));
const ReviewLanding = React.lazy(() => import("../Pages/Review/landing"));
const SurveyLanding = React.lazy(() => import("../Pages/Survey/landing"));
const DailyDealsLanding = React.lazy(
  () => import("../Pages/DailyDeals/landing")
);
const InstantRewardsLanding = React.lazy(
  () => import("../Pages/InstantRewards/landing")
);
const PurchaseRewardLanding = React.lazy(
  () => import("../Pages/PurchaseRewards/landing")
);

export const BRAND_PAGE_TITLE_DELIMITER = " - ";
const PAGE_VIEW_EVENT = AnalyticsTrackingEvent.PAGE_VIEW;
const PAGE_ID = {
  SUMMARY: "summary",
  SEARCH: "AllProducts",
};

const isInternalCompany = (companyId: string): boolean => {
  switch (ConfigUtils.getAppStage()) {
    case "prod":
      return companyId === "37d2cad0-2598-45c8-a922-f7917588ec62";
    case "beta":
    case "gamma":
    case "localhost":
      return companyId === "42e88439-b631-44ed-8857-34feca34f403";
  }
};

const isBrandDefaultLanding = (page: Page, domainBrandId?: number) => {
  if (page?.route === "/home" && domainBrandId === BRANDCLUB_BRAND_ID) {
    return true;
  }
  return false;
};

export default function BrandStoreLandingPage() {
  const storeBrandingType = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig.storeBrandingType
  );
  const mainEntity = useAppSelector(({ mainEntity }) => mainEntity);
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);

  const {
    site,
    persistentQueryParams,
    abTestingTreatments,
    brandId: brandIdFromSiteContext,
  } = useSiteContext();
  const params = useParams();
  const { brandId: brandIdFromParams, sku, categoryId, subCategoryId } = params;
  const brandclubIds = useMemo(
    () =>
      brandEntity?.entity?.brandclubId != null
        ? [brandEntity.entity.brandclubId]
        : undefined,
    [brandEntity?.entity?.brandclubId]
  );
  const brandIds = useMemo(
    () => getBrandIds(brandEntity?.entity),
    [brandEntity?.entity]
  );

  const brandId = brandIdFromSiteContext
    ? `${brandIdFromSiteContext}`
    : brandIdFromParams;

  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      document.title = "Brandclub - Get rewarded to shop";
    };
  }, []);

  useEffect(() => {
    if (brandId) {
      dispatch(getBrandEntity({ id: `${brandId}` }));
      dispatch(getBrandclubEntity({ id: +brandId }));
    }
  }, [brandId, dispatch]);

  // check for the following conditions
  // site or mainEntity or brandEntity not loaded
  // mainEntity id doesn't match the current url param

  if (
    !storeBrandingType ||
    !site ||
    !mainEntity ||
    !brandEntity ||
    (sku ? `${mainEntity.id}` !== sku : `${mainEntity.id}` !== brandId)
  ) {
    return (
      <div>
        <StoreFullScreenLoading storeHeader={false} />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<DynamicPageLayout />}>
          {generateNestedRoutes({
            childPages: site.pages,
            mainEntity,
            brandEntity,
            site,
            persistentQueryParams,
            pageContext: {
              brandId,
              sku,
              categoryId,
              subCategoryId,
              abTestingTreatments,
            },
          })}
          <Route path="dailydeals/" element={<DailyDealsLanding />} />,
          <Route
            path="surveys/"
            element={<SurveyLanding brandclubIds={brandclubIds} />}
          />
          <Route
            path="productreviews/"
            element={<ReviewLanding brandIds={brandIds} />}
          />
          <Route
            path="contentrewards/"
            element={<ContentLanding brandclubIds={brandclubIds} />}
          />
          <Route
            path="instantrewards/"
            element={<InstantRewardsLanding brandIds={brandIds} />}
          />
          <Route
            path="affiliate/"
            element={<AffiliateLanding brandIds={brandIds} />}
          />
          <Route
            path="popular/"
            element={<PopularLanding brandIds={brandIds} />}
          />
          <Route
            path="purchaserewards/"
            element={<PurchaseRewardLanding brandIds={brandIds} />}
          />
          <Route
            path="branddashboard/"
            element={<DashboardOutlet brand topOffset={0} />}
          >
            <Route path="" element={<Navigate to="me" />} />
            <Route path="me" index element={<Dashboard brand />} />
            <Route path="purchase/">
              <Route path="" element={<PurchaseList />} />
              <Route path=":batchId" element={<PurchaseDetail />} />
            </Route>

            <Route path="rewards/">
              <Route path="balance" element={<Balance />} />
              <Route path="activity" index element={<Activity />} />
              <Route path="expiring" element={<Expiring />} />

              <Route path="payouts/">
                <Route path="" element={<Payouts />} />
                <Route path=":transactionId" element={<PayoutDetail />} />
              </Route>

              <Route path="" element={<Navigate to="activity" />} />
              <Route path="*" element={<Navigate to="activity" />} />
            </Route>
          </Route>
          <Route path={"offers"} element={<Offers />} />
        </Route>
      </Routes>
    </>
  );
}

const generateNestedRoutes = ({
  childPages,
  mainEntity,
  brandEntity,
  site,
  persistentQueryParams,
  pageContext,
}: {
  childPages: Page[];
  mainEntity: IReduxStoreBase["mainEntity"];
  brandEntity: IReduxStoreBase["mainEntity"];
  site: Site;
  persistentQueryParams: string | undefined;
  pageContext: PageContext;
}) => {
  if (!childPages?.length) {
    return null;
  }

  const productPage = childPages.find((p) => p.route === "/product");

  return (
    <>
      {pageContext.sku && productPage ? (
        <Route
          path={"/"}
          element={
            <RenderDynamicPage
              page={productPage}
              site={site}
              brandEntity={brandEntity}
              pageContext={{ ...pageContext }}
              persistentQueryParams={persistentQueryParams}
            />
          }
        />
      ) : null}
      {childPages.map((child: any, idx: number) => {
        const pathname = child.route; // route should be passed from builder
        const categoryId = (() => {
          const regex = /\/categories\/([0-9]+)/;
          const matches = pathname.match(regex);
          if (Array.isArray(matches) && matches.length >= 2) {
            return matches[1];
          }
          return undefined;
        })();
        const subCategoryId = (() => {
          const regex = /\/subcategories\/([0-9]+)/;
          const matches = pathname.match(regex);
          if (Array.isArray(matches) && matches.length >= 2) {
            return matches[1];
          }
          return undefined;
        })();
        return (
          <React.Fragment key={idx}>
            <Route
              path={child.route}
              element={
                <RenderDynamicPage
                  page={child}
                  site={site}
                  brandEntity={brandEntity}
                  pageContext={{ ...pageContext, categoryId, subCategoryId }}
                  persistentQueryParams={persistentQueryParams}
                />
              }
            />
            {child.route === "/home" && (
              <Route
                path={"/"}
                element={
                  <RenderDynamicPage
                    page={child}
                    site={site}
                    brandEntity={brandEntity}
                    pageContext={{ ...pageContext, categoryId, subCategoryId }}
                    persistentQueryParams={persistentQueryParams}
                  />
                }
              />
            )}
            {child.childPages &&
              generateNestedRoutes({
                childPages: child.childPages,
                mainEntity,
                brandEntity,
                site,
                persistentQueryParams,
                pageContext,
              })}
          </React.Fragment>
        );
      })}
    </>
  );
};

const WIDGET_KEY_SEP = "-";

export const getWidgetKey = (widgetId: string, index: number) =>
  `${widgetId}${WIDGET_KEY_SEP}${index}`;

const DynamicPageLayout = () => {
  return (
    <div>
      <Outlet />
    </div>
  );
};

const RenderDynamicPage = ({
  page,
  site,
  persistentQueryParams,
  pageContext,
  brandEntity,
}: {
  page: Page;
  site: Site;
  brandEntity: IReduxStoreBase["mainEntity"];
  persistentQueryParams: string | undefined;
  pageContext: PageContext;
}) => {
  const { signedIn } = useSignedIn();
  const apolloClient = useApolloClient();
  const mainEntity = useAppSelector(({ mainEntity }) => mainEntity);
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );
  /**
   * for pdp, customSitePage is always returned for PUBLISHED state.
   * use page over customSitePage in preview mode (DRAFT state)
   * */
  const pageToUse: Page =
    mainEntity?.type === "product" &&
    persistentQueryParams?.includes("mode=preview")
      ? page
      : mainEntity?.entity?.customSitePage || page;
  const [widgets] = useStateWithDep(pageToUse?.widgets || []);
  const [categoryEntity, setCategoryEntity] =
    useState<
      | {
          type: string;
          entity: any;
          id: string | number;
        }
      | undefined
    >();
  const [subCategoryEntity, setSubCategoryEntity] =
    useState<
      | {
          type: string;
          entity: any;
          id: string | number;
        }
      | undefined
    >();
  const [brandclub, setBrandclub] = useState<BrandClub | undefined>(undefined);

  const [pageViewEventPayload, setPageViewEventPayload] = useState<{
    eventName: AnalyticsTrackingEvent | undefined;
    eventPayload: any;
  }>({ eventName: undefined, eventPayload: null });
  const [trackingParams] = useLocationTrackingParams();

  useEffect(() => {
    (async () => {
      if (pageContext.categoryId) {
        if (
          !categoryEntity ||
          `${categoryEntity?.id}` !== `${pageContext.categoryId}`
        ) {
          const { data } = await getEntityForLandingPage({
            entityId: pageContext.categoryId,
            entityType: "category",
          });
          setCategoryEntity({
            entity: data,
            id: data.categoryId,
            type: BrandclubEntityType.Category,
          });
        }
      } else if (categoryEntity) {
        setCategoryEntity(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.categoryId]);

  useEffect(() => {
    (async () => {
      if (pageContext.subCategoryId) {
        if (
          !subCategoryEntity ||
          `${subCategoryEntity?.id}` !== `${pageContext.subCategoryId}`
        ) {
          const { data } = await getEntityForLandingPage({
            entityId: pageContext.subCategoryId,
            entityType: "subCategory",
          });
          setSubCategoryEntity({
            entity: data,
            id: data.subCategoryId,
            type: BrandclubEntityType.Subcategory,
          });
        }
      } else if (subCategoryEntity) {
        setSubCategoryEntity(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.subCategoryId]);

  useEffect(() => {
    const fetchBrandclub = async (brandId: number) => {
      const { data } = await apolloClient.query({
        query: getBrandClubForBrandPublic,
        variables: { brandId },
        fetchPolicy: "no-cache",
      });
      if (data?.BrandClubForBrandPublic) {
        setBrandclub(data?.BrandClubForBrandPublic);
      }
    };

    if (brandEntity?.id) {
      fetchBrandclub(parseInt(`${brandEntity.id}`));
    }
  }, [brandEntity?.id, apolloClient]);

  useEffect(() => {
    let title = page.title;
    if (page.title.endsWith("- Product Title") && mainEntity?.entity?.title) {
      title = `${brandEntity?.entity?.name}${BRAND_PAGE_TITLE_DELIMITER}Brandclub${BRAND_PAGE_TITLE_DELIMITER}${mainEntity.entity.title}`;
    }

    document.title = title;
    const el = document.querySelector("meta[name='description']");
    if (el) {
      el.setAttribute("content", page?.metaDescription);
    }
  }, [
    page.title,
    mainEntity?.entity?.title,
    brandEntity?.entity?.name,
    page?.metaDescription,
  ]);

  useEffect(() => {
    if (brandEntity && brandEntity.entity) {
      let pageId: string | undefined = pageContext.brandId;
      const additionalParams: Record<string, string> = {};
      switch (pageToUse.pageType) {
        case BrandclubPageType.Home:
          pageId = PAGE_ID.SUMMARY;
          break;
        case BrandclubPageType.Category:
          pageId = pageContext.categoryId;
          break;
        case BrandclubPageType.Subcategory:
          pageId = pageContext.subCategoryId;
          break;
        case BrandclubPageType.Product:
          pageId = pageContext.sku;
          Object.assign(additionalParams, { stacklineSku: pageContext.sku });
          break;
        case BrandclubPageType.Collection:
          pageId = pageToUse.id ?? PAGE_ID.SUMMARY;
          break;
        case BrandclubPageType.Editorial:
          pageId = pageToUse.id ?? PAGE_ID.SUMMARY;
          break;
        case BrandclubPageType.Search:
          {
            const { location } = window;
            const params = queryString.parse(location.search, {
              ignoreQueryPrefix: true,
              arrayLimit: 100,
            });
            if (
              params.titleInclude &&
              params.titleInclude.length &&
              (params.titleInclude as any).length > 0
            ) {
              pageId = (params.titleInclude as any)[0];
            } else {
              pageId = PAGE_ID.SEARCH;
            }
          }
          break;
        default:
          pageId = pageContext.brandId;
          break;
      }
      setPageViewEventPayload({
        eventName: PAGE_VIEW_EVENT,
        eventPayload: {
          ...trackingParams,
          pageType: pageToUse.pageType,
          pageId,
          ...pageContext,
          brandId: brandEntity.entity.id,
          brandclubId: brandEntity.entity.brandclubId,
          ...additionalParams,
          brandPixels: {
            brandId: mainEntity?.entity?.brandId,
            pixels: mainEntity?.entity?.pixels,
          },
        },
      });
    }
  }, [pageContext, pageToUse, site, brandEntity, mainEntity, trackingParams]);

  useDeepCompareEffect(() => {
    if (pageViewEventPayload.eventName && pageViewEventPayload?.eventPayload) {
      (async () => {
        const analyticsResult = await AnalyticsService.track(
          pageViewEventPayload.eventName!,
          pageViewEventPayload.eventPayload
        );
        if (analyticsResult) {
          const { eventName, eventParams } = analyticsResult;
          if (
            (eventName === AnalyticsTrackingEvent.PAGE_VIEW ||
              eventName?.endsWith("openRetailerProductPage")) &&
            eventParams?.pageType === BrandclubPageType.Product
          ) {
            try {
              const stacklineSku =
                eventParams.stacklineSku || eventParams.pageId;
              if (stacklineSku && signedIn) {
                const data = { ...eventParams, eventName };
                const url = `/recentlyViewed/setProduct?sku=${stacklineSku}`;
                await axios.post(url, data, {
                  headers: {
                    ...(await getAuthorizationHeader()),
                  },
                });
              }
            } catch (e) {
              console.error("Error registering page view: ", JSON.stringify(e));
            }
          }
        }
      })();
    }
  }, [pageViewEventPayload, signedIn]);

  if (isBrandDefaultLanding(page, domainConfig?.brandId)) {
    return <BrandstoreHomepage pageContext={pageContext} />;
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {widgets.map((widget, idx) => {
        const key = getWidgetKey(widget.widgetId, idx);

        if (
          widget.widgetId === "Brandclub_Navigation" ||
          widget.widgetId === "Brandclub_Footer"
        ) {
          return null;
        } // this should be removed once from the data site

        // hide brandclub footer for custom store

        return (
          <RenderWidgetWrapper
            key={key}
            widget={widget}
            page={pageToUse}
            site={site}
            brandEntity={brandEntity}
            pageContext={{
              ...pageContext,
              categoryEntity,
              subCategoryEntity,
            }}
            persistentQueryParams={persistentQueryParams}
            brandclub={brandclub}
          />
        );
      })}
    </div>
  );
};

const RenderWidgetWrapper = ({
  widget,
  page,
  site,
  persistentQueryParams,
  pageContext,
  brandEntity,
  brandclub,
}: {
  widget: Widget;
  page: Page;
  site: Site;
  persistentQueryParams: string | undefined;
  brandEntity: IReduxStoreBase["mainEntity"];
  pageContext: PageContext;
  brandclub?: BrandClub;
}) => {
  const apolloClient = useApolloClient();
  const reduxState = useAppSelector((state) => state);
  const customerSpendByBrand = useAppSelector(
    ({ customerSpendByBrand }) => customerSpendByBrand
  );
  const { routePrefix } = useSiteContext();
  const [trackingParams] = useLocationTrackingParams();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const dispatch = useAppDispatch();
  const isLoggedOut = !userProfile;

  const { openDrawer } = useCheckoutDrawer();
  const mainEntity = reduxState["mainEntity"];
  const generatedWidgetProps = buildWidgetProps({
    widget,
    reduxState,
    persistentQueryParams,
    apolloClient,
  });

  const onShareClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    product?: ProductRewardSearchResult
  ) => {
    e.preventDefault();
    if (product) {
      ShareProductDialog.show(product, isLoggedOut);
    }
  };

  const handleJoinClub = async (brandclubId: string, brandId?: number) => {
    try {
      const res = await joinBrandClub({
        brandclubId: brandclubId,
      });
      if (res.status === 200 && res.data && res.data.status === "success") {
        // await dispatch(getBrandclubEntity({ id: +brandId }));
        return "success";
      }
      return "failed";
    } catch (e) {
      console.error("join club error: ", e);
      return "failed";
    }
  };

  const widgetProps: any = {
    LinkComponent: Link,
    useSearchParams,
    useNavigate,
    mainEntity,
    brandEntity,
    trackingParams,
    analyticsTrack: async (
      eventName: AnalyticsTrackingEvent,
      eventPayload: any
    ) => {
      await AnalyticsService.track(eventName, eventPayload);
    },
    persistentQueryParams,
    axios,
    axiosEdgeCached,
    apolloClient,
    page,
    site,
    pageContext,
    brandclub,
    isInternalCompany: brandclub && isInternalCompany(brandclub.companyId),
    retailers: reduxState.retailers,
    appConfig: reduxState.appConfig,
    routePrefix,
    onShareClick,
    onAddToCart: async (item: {
      brandId: number;
      retailerSku: string;
      retailerId: number;
      stacklineSku: string;
      retailPrice: number;
      retailerParentSku: string;
      retailerProductPageUrl: string;
      checkoutEligibility?: CheckoutEligibility;
    }) => {
      if (isServerSideCheckoutEligible(item.checkoutEligibility)) {
        const addItemsSuccess = dispatch(
          addOptimisticShoppingCartItem({
            items: [
              {
                ...item,
                quantity: 1,
              },
            ],
            retailerId: item.retailerId,
            totalItems: 1,
          })
        );

        addItemsSuccess && openDrawer();
      } else {
        window.open(item.retailerProductPageUrl, "_blank");
      }
    },
    userProfile,
    customerSpendByBrand,
    handleJoinClub,
    ...generatedWidgetProps,
  };

  const Component = WidgetIdToComponentMapping[widget.widgetId];
  Component.displayName = widget.widgetId;

  const style = useMemo(() => {
    if (widget.forceFullWidth || _isNil(page?.containerConfigOverride?.width)) {
      return {
        width: "100%",
      };
    }

    return {
      width: "100%",
      maxWidth: page?.containerConfigOverride?.width,
    };
  }, [page?.containerConfigOverride?.width, widget.forceFullWidth]);

  if (routePrefix === null || routePrefix === undefined) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <div style={style}>
        <Component {...widgetProps} />
      </div>
    </ErrorBoundary>
  );
};
